import React, { useCallback, useMemo, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import useOnClickOutside from "use-onclickoutside";
import { icon } from "../../../config";
import { MULTI_DIMENSION_TYPE_OPTIONS, VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "./workflowVariables.constants";

const ANCHOR_ALIGN = { horizontal: "left", vertical: "bottom" };
const POPUP_ALIGN = { horizontal: "left", vertical: "top" };

/** True if click is on a Kendo portaled popup (e.g. DropDownList list). */
const isKendoOverlayTarget = (target) => {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest(".k-popup") || target.closest(".k-animation-container") || target.closest(".k-child-animation-container"));
};

/** Column add/edit: anchored Kendo Popup; ref + useOnClickOutside closes on outside click. */
/** @author: Sejal */
const MultiDimensionColumnPopup = ({
  anchorRef,
  mode,
  popupName,
  nameMax,
  columnType,
  saveDisabledAdd,
  saveDisabledUpdate,
  onClose,
  onNameChange,
  onTypeChange,
  onCancel,
  onSaveAdd,
  onUpdateColumn,
  onDeleteColumn,
}) => {
  const panelRef = useRef(null);

  /** Same pattern as delete-role-popup: clicks inside panelRef are "inside". */
  const hideOnClickOutside = useCallback(
    (e) => {
      if (isKendoOverlayTarget(e?.target)) return;
      onClose();
    },
    [onClose]
  );

  useOnClickOutside(panelRef, hideOnClickOutside);

  const typeOptionsWithIcons = useMemo(
    () =>
      MULTI_DIMENSION_TYPE_OPTIONS.map((o) => ({
        ...o,
        icon: VARIABLE_ICONS[o.value] || VARIABLE_ICONS.text,
      })),
    []
  );

  const selectedTypeItem = useMemo(
    () => typeOptionsWithIcons.find((o) => o.value === columnType) ?? typeOptionsWithIcons[0],
    [columnType, typeOptionsWithIcons]
  );

  const itemRender = useCallback(
    (li, itemProps) =>
      React.cloneElement(
        li,
        li.props,
        <span className='d-flex align-items-center gap-2'>
          {itemProps.dataItem.icon}
          {itemProps.dataItem.label}
        </span>
      ),
    []
  );

  /** Keep Kendo's `k-input-inner` span so the picker stays flex; grow it so the caret stays at the end. */
  const valueRender = useCallback((element, selectedValue) => {
    if (!selectedValue?.label) return element;
    return React.cloneElement(element, {
      ...element.props,
      className: `${element.props?.className || ""}  d-flex align-items-center gap-2`.trim(),
      style: { ...(element.props?.style || {}), minWidth: 0 },
      children: (
        <>
          {selectedValue.icon}
          <span className='k-input-value-text'>{selectedValue.label}</span>
        </>
      ),
    });
  }, []);

  const anchor = anchorRef?.current;
  if (!anchor) return null;

  return (
    <Popup
      anchor={anchor}
      show
      animate
      anchorAlign={ANCHOR_ALIGN}
      popupAlign={POPUP_ALIGN}
      collision={{ horizontal: "fit", vertical: "flip" }}
      className='multi-dimension-column-popup-root'
      popupClass='multi-dimension-column-popup-kendo-inner'>
      <div ref={panelRef} className=' shadow multi-dimension-column-popup-card' role='dialog' aria-labelledby='multi-dim-column-dialog-title'>
        <div className='card-body p-3'>
          {mode === "edit" && (
            <div className='d-flex justify-content-end mb-2'>
              <button
                type='button'
                className='btn btn-link p-0 text-danger'
                onClick={onDeleteColumn}
                title={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_DELETE_COLUMN}
                aria-label={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_DELETE_COLUMN}>
                {icon.DELETE_FOREVER_ICON}
              </button>
            </div>
          )}
          <div className='mb-3'>
            <label className='small text-primary mb-1 d-block' id='multi-dim-column-dialog-title'>
              {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_COLUMN_NAME}
            </label>
            <Input className='form-control form-control-sm' value={popupName} maxLength={nameMax} onChange={(e) => onNameChange(e.target.value)} />
            <small className='text-muted'>
              {popupName.length}/{nameMax}
            </small>
          </div>
          <div className='mb-3'>
            <label className='small text-muted mb-1 d-block'>{WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_COLUMN_TYPE}*</label>
            <DropDownList
              className='w-100'
              data={typeOptionsWithIcons}
              textField='label'
              dataItemKey='value'
              value={selectedTypeItem}
              onChange={onTypeChange}
              itemRender={itemRender}
              valueRender={valueRender}
            />
          </div>
          <div className='d-flex justify-content-between gap-2 mt-3'>
            <Button fillMode='outline' className='flex-grow-1' onClick={onCancel}>
              {WORKFLOW_VARIABLE_LABELS.CANCEL}
            </Button>
            {mode === "add" ? (
              <Button themeColor='primary' className='flex-grow-1' onClick={onSaveAdd} disabled={saveDisabledAdd}>
                {WORKFLOW_VARIABLE_LABELS.SAVE}
              </Button>
            ) : (
              <Button themeColor='primary' className='flex-grow-1' onClick={onUpdateColumn} disabled={saveDisabledUpdate}>
                {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_UPDATE_COLUMN}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default MultiDimensionColumnPopup;
